import { 
  LayoutDashboard, 
  ListTodo, 
  ShoppingBag,
  Users, 
  Map, 
  FileText,
  Calendar,
  UserCheck,
  Building,
  Building2,
  Stethoscope,
  Car,
  Home,
  Wrench,
  Settings,
  Shield,
  ContactRound,
  MapPin,
  UserPlus,
  Search,
  PackageCheck,
  Truck,
  Activity
} from "lucide-react";

export interface NavigationItem {
  title: string;
  url: string;
  icon: any;
}

export interface NavigationItemGroup {
  title: string;
  icon: any;
  /** Children may be leaf links or a nested group (e.g. Logistics → Fleet → Vehicles). */
  children: NavigationEntry[];
}

export type NavigationEntry = NavigationItem | NavigationItemGroup;

export const isNavigationGroup = (entry: NavigationEntry): entry is NavigationItemGroup =>
  'children' in entry;

export interface NavigationConfig {
  appName: string;
  navigation: NavigationEntry[];
}

export const navigationConfigs: Record<string, NavigationConfig> = {
  // Food & Beverage / Delivery
  food_beverage: {
    appName: "Delivery Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      {
        title: "Orders",
        icon: ShoppingBag,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Order Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      { title: "Deliveries", url: "/tasks-list", icon: ListTodo },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Drivers", url: "/drivers-management", icon: Users },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  },

  // Healthcare
  healthcare: {
    appName: "Healthcare Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Appointments", url: "/tasks-list", icon: Calendar },
      {
        title: "Patients",
        icon: Users,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Patient Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Medical Staff", url: "/drivers-management", icon: UserCheck },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  },

  // Real Estate
  real_estate: {
    appName: "Property Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Showings", url: "/tasks-list", icon: Calendar },
      {
        title: "Properties",
        icon: Building,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Property Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Agents", url: "/drivers-management", icon: Users },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  },

  // Field Services
  field_services: {
    appName: "Service Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Service Calls", url: "/tasks-list", icon: Wrench },
      {
        title: "Work Orders",
        icon: FileText,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Work Order Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Technicians", url: "/drivers-management", icon: Users },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  },

  // Transportation
  transportation: {
    appName: "Fleet Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Routes", url: "/tasks-list", icon: Map },
      {
        title: "Bookings",
        icon: Calendar,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Booking Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Drivers", url: "/drivers-management", icon: Users },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  },

  // Logistics
  logistics: {
    appName: "Logistics Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Tasks", url: "/tasks-list", icon: ListTodo },
      {
        title: "Orders",
        icon: ShoppingBag,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Order Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Drivers", url: "/drivers-management", icon: Users },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  },

  // Default/Generic
  other: {
    appName: "Business Manager",
    navigation: [
      { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
      { title: "Tasks", url: "/tasks-list", icon: ListTodo },
      {
        title: "Orders",
        icon: ShoppingBag,
        children: [
          { title: "POS Sync", url: "/orders/pos-sync", icon: ShoppingBag },
          { title: "Order Management", url: "/orders/order-management", icon: Settings },
        ]
      },
      {
        title: "Logistics",
        icon: Truck,
        children: [
          { title: "Logistics Requests", url: "/logistics-requests", icon: PackageCheck },
          { title: "Route Management", url: "/route-management", icon: MapPin },
          { title: "Drivers", url: "/drivers-management", icon: Users },
          {
            title: "Fleet",
            icon: Truck,
            children: [
              { title: "Vehicles", url: "/fleet/vehicles", icon: Truck },
            ]
          },
        ]
      },
      { title: "Live Map", url: "/map", icon: Map },
      { title: "Track & Trace", url: "/track", icon: Search },
      { title: "Reports", url: "/reports", icon: FileText },
    ]
  }
};

// Fallback configuration
export const getNavigationConfig = (businessType?: string | null): NavigationConfig => {
  if (!businessType) {
    return navigationConfigs.other;
  }
  
  return navigationConfigs[businessType] || navigationConfigs.other;
};

export type AdminNavigationEntry = NavigationItem | NavigationItemGroup;

export const adminNavigationItems: AdminNavigationEntry[] = [
  { title: "User Management", url: "/admin/users", icon: UserPlus },
  {
    title: "Outlet Management",
    icon: Building2,
    children: [
      { title: "Outlet Listing", url: "/admin/outlets", icon: Building2 },
      { title: "Outlet Staff", url: "/admin/outlet-staff", icon: Users },
    ]
  },
  {
    title: "Factory Management",
    icon: Building,
    children: [
      { title: "Factory Listing", url: "/admin/factory-management/listing", icon: Building },
      { title: "Factory Staff", url: "/admin/factory-management/staff", icon: Users },
    ]
  },
  { title: "Team Management", url: "/admin/teams", icon: Users },
  { title: "Role Management", url: "/admin/roles", icon: Shield },
  { title: "Security Events", url: "/admin/security-events", icon: Shield },
  { title: "System Settings", url: "/admin/settings", icon: Settings },
  { title: "Monitoring", url: "/monitoring", icon: Shield },
  { title: "Data Health", url: "/data-health", icon: Activity }
];
